import { useMutation } from '@apollo/react-hooks'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { historyVariables } from '../lib/records'
import type { CostApplication, CostInvoice } from '../types'
import { WRITE, useCostWriteQueue, type Mutate, type Reload, type Vars } from './useCostWriteQueue'

/**
 * Payment application writes (1b-A spec §7). Each reloads the application, its history and its contract's detail
 * (where the list and the retention live), also after a refusal; `onDetailError` hears when the application is gone.
 */
export function useApplicationMutations(projectId: string, contractId: string, onDetailError?: (applicationId: string, error: unknown) => void) {
  const { saving, run } = useCostWriteQueue()
  const [create] = useMutation(Mutation.CREATE_COST_APPLICATION, WRITE)
  const [update] = useMutation(Mutation.UPDATE_COST_APPLICATION, WRITE)
  const [remove] = useMutation(Mutation.DELETE_COST_APPLICATION, WRITE)
  const [setLines] = useMutation(Mutation.SET_COST_APPLICATION_LINES, WRITE)
  const [submit] = useMutation(Mutation.SUBMIT_COST_APPLICATION, WRITE)
  const [returnIt] = useMutation(Mutation.RETURN_COST_APPLICATION, WRITE)
  const [saveReview] = useMutation(Mutation.SAVE_COST_APPLICATION_REVIEW, WRITE)
  const [certify] = useMutation(Mutation.CERTIFY_COST_APPLICATION, WRITE)
  const [uncertify] = useMutation(Mutation.UNCERTIFY_COST_APPLICATION, WRITE)
  const [invoice] = useMutation(Mutation.CREATE_COST_INVOICE_FROM_APPLICATION, WRITE)
  const [setInvestorInvoice] = useMutation(Mutation.SET_COST_APPLICATION_INVOICE, WRITE)
  const [addAttachment] = useMutation(Mutation.ADD_COST_ATTACHMENT, WRITE)
  const [removeAttachment] = useMutation(Mutation.REMOVE_COST_ATTACHMENT, WRITE)
  const contract: Reload[] = [{ query: Query.COST_CONTRACT, variables: { id: contractId } }]
  const detail = (id: string): Reload[] => [
    { query: Query.COST_APPLICATION, variables: { id }, onError: onDetailError && ((e: unknown) => onDetailError(id, e)) },
    { query: Query.COST_ACTIVITY_LOG, variables: historyVariables(projectId, 'APPLICATION', id) },
    ...contract,
  ]
  const m = (fn: unknown) => fn as Mutate
  type Review = { key: string; certified: string; note: string | null }[]

  return {
    saving,
    create: (input: Vars) => run<{ createCostApplication: CostApplication }>(m(create), { contractId, input }, contract),
    update: (id: string, input: Vars) => run(m(update), { id, input }, detail(id)),
    remove: (id: string) => run(m(remove), { id }, contract, detail(id)),
    setLines: (id: string, lines: { key: string; requested: string }[], manualLines?: Vars[]) => run(m(setLines), { id, lines, manualLines }, detail(id)),
    submit: (id: string) => run(m(submit), { id }, detail(id)),
    returnIt: (id: string, note: string) => run(m(returnIt), { id, note }, detail(id)),
    saveReview: (id: string, lines: Review, manualRows?: Vars[]) => run(m(saveReview), { id, lines, manualRows }, detail(id)),
    certify: (id: string, lines: Review | null) => run(m(certify), { id, lines }, detail(id)),
    uncertify: (id: string) => run(m(uncertify), { id }, detail(id)),
    invoice: (id: string, input: Vars) => run<{ createCostInvoiceFromApplication: CostInvoice }>(m(invoice), { applicationId: id, input }, [
      ...detail(id), { query: Query.COST_INVOICES, variables: { projectId, filter: { contractId } } },
    ]),
    /** `input` null removes the recorded investor invoice. */
    setInvestorInvoice: (id: string, input: Vars | null) => run(m(setInvestorInvoice), { applicationId: id, input }, detail(id)),
    addAttachment: (id: string, source: { fileId: string } | { docRevisionId: string }) => run(m(addAttachment), { target: { applicationId: id }, ...source }, detail(id)),
    removeAttachment: (id: string, attachmentId: string) => run(m(removeAttachment), { id: attachmentId }, detail(id)),
  }
}

export type ApplicationMutations = ReturnType<typeof useApplicationMutations>
